import { BoardConfig } from './board.types';

/**
 * Board Configurations (Fully Config-Driven)
 *
 * Angular 21+ 지향 설정:
 * - columns 추가로 테이블 구조를 설정만으로 정의
 * - HTML 수정 없이 컬럼 추가/제거/순서 변경 가능
 * - Type-safe column definitions
 *
 * Legacy 투명 마이그레이션:
 * - URL: /bbs/* (Legacy와 동일)
 * - 기능: List, Search, Create, Detail
 * - 데이터: IBbs {id, title, content, createDate, viewCount, topFl, specialFl}
 */
export const BOARD_CONFIGS: Record<string, BoardConfig> = {
  notice: {
    title: 'brandFn.menu.bbs_notice',
    type: '공지사항',
    viewType: 'table',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
      {
        name: 'topFl',
        label: 'REPO.TABLE.bbs.topFl',
        type: 'select',
        options: [{ value: 'true', label: 'REPO.TABLE.bbs.topFl.fixed' }],
      },
      {
        name: 'specialFl',
        label: 'REPO.TABLE.bbs.specialFl',
        type: 'select',
        options: [
          { value: 'true', label: 'REPO.TABLE.bbs.specialFl.important' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.bbs.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
      {
        key: 'viewCount',
        name: 'REPO.TABLE.bbs.viewCount',
        type: 'number',
        width: '100px',
        class: 'text-end',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/notice',
    permissions: ['BBS$READ'],
    features: ['bbs_notice'],
  },
  event: {
    title: 'brandFn.menu.bbs_event',
    type: '이벤트',
    viewType: 'grid',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
      {
        name: 'topFl',
        label: 'REPO.TABLE.bbs.topFl',
        type: 'select',
        options: [{ value: 'true', label: 'REPO.TABLE.bbs.topFl.fixed' }],
      },
      {
        name: 'specialFl',
        label: 'REPO.TABLE.bbs.specialFl',
        type: 'select',
        options: [
          { value: 'true', label: 'REPO.TABLE.bbs.specialFl.important' },
        ],
      },
    ],
    columns: [
      { key: 'id', name: 'REPO.TABLE.bbs.id', type: 'number', width: '80px', class: 'text-center', sortable: true },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
      {
        key: 'viewCount',
        name: 'REPO.TABLE.bbs.viewCount',
        type: 'number',
        width: '100px',
        class: 'text-end',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/event',
    permissions: ['BBS$READ'],
    features: ['bbs_event'],
  },
  faq: {
    title: 'brandFn.menu.bbs_faq',
    type: 'FAQ',
    viewType: 'accordion',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.question' },
      {
        name: 'topFl',
        label: 'REPO.TABLE.bbs.topFl',
        type: 'select',
        options: [{ value: 'true', label: 'REPO.TABLE.bbs.topFl.fixed' }],
      },
      {
        name: 'specialFl',
        label: 'REPO.TABLE.bbs.specialFl',
        type: 'select',
        options: [
          { value: 'true', label: 'REPO.TABLE.bbs.specialFl.important' },
        ],
      },
    ],
    columns: [
      { key: 'id', name: 'REPO.TABLE.bbs.id', type: 'number', width: '80px', class: 'text-center', sortable: true },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.question',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/faq',
    permissions: ['BBS$READ'],
    features: ['bbs_faq'],
  },
  column: {
    title: 'brandFn.menu.bbs_column',
    type: 'COLUMN',
    viewType: 'grid',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
    ],
    columns: [
      { key: 'id', name: 'REPO.TABLE.bbs.id', type: 'number', width: '80px', class: 'text-center', sortable: true },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/column',
  },
  review: {
    title: 'brandFn.menu.bbs_review',
    type: 'REVIEW',
    viewType: 'table',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.bbs.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
      {
        key: 'viewCount',
        name: 'REPO.TABLE.bbs.viewCount',
        type: 'number',
        width: '100px',
        class: 'text-end',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/review',
  },
  // featured_member 제거 (2026-05-12): 신규 도메인 features/featured-member 로 분리됨
  // 🆕 추가된 게시판들 (Legacy에서 Modern으로 마이그레이션)
  cms: {
    title: 'brandFn.menu.bbs_cms',
    type: 'COMMON',
    viewType: 'table',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.bbs.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/cms',
    permissions: ['BBS$READ'],
  },
  hmpg: {
    title: 'brandFn.menu.bbs_homepage',
    type: 'HMPG',
    viewType: 'table',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.bbs.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/hmpg',
    permissions: ['BBS$READ'],
  },
  popup: {
    title: 'brandFn.menu.bbs_popup',
    type: 'POPUP',
    viewType: 'table',
    searchFields: [
      { name: 'id', type: 'text', label: 'REPO.TABLE.bbs.id' },
      { name: 'title', type: 'text', label: 'REPO.TABLE.bbs.title' },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.bbs.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'title',
        name: 'REPO.TABLE.bbs.title',
        type: 'text',
        sortable: true,
        filterable: true,
      },
      {
        key: 'createDate',
        name: 'REPO.TABLE.bbs.createDate',
        type: 'date',
        width: '120px',
        class: 'text-center',
        dateFormat: 'yyyy-MM-dd',
        sortable: true,
      },
    ],
    defaultPageSize: 10,
    defaultSort: 'id,desc',
    routePath: 'bbs/popup',
    permissions: ['HOME_SCREEN$READ'],
  },
};
